<template>
  <main-layout
    customControls
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <div id="header">
            <h1 class="header-title">
              <chevron
                @click="$router.push({name: 'LicenseManagement'})"/>
              {{ $t('health.license.new') }}
            </h1>
          </div>
        </grid-col>
      </grid-row>
      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave"
        v-on:keydown.space.prevent="'false'">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="emailInput"
                form-size="m"
                :label="$t('signin.email')"
                v-model="email"
                :type="'text'"
                :error="emailError"
                :errorMessage="$t('signin.notValidEmail')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="couponInput"
                form-size="m"
                :label="$t('health.license.coupon')"
                v-model="coupon"
                :type="'text'"
                :error="couponError"
                :errorMessage="$t('health.license.invalidCoupon')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="productsTable"
              :data="fetchData"
              :show-caption="false"
              sort-by="productId"
              sort-order="asc"
              class="datatable"
              :show-filter="true">
              <table-column show="id" label="Id" :hidden="true"></table-column>
              <table-column
                show="productId"
                :label="$t('health.license.productId')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="description"
                :label="$t('health.license.description')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="entitlement"
                :label="$t('health.license.entitlement')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="duration"
                :label="$t('health.license.duration')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="amount"
                :label="$t('health.license.amount')"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="addOne(row)"
                    :icon="icon.plus"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-on:click="substractOne(row)"
                    :icon="icon.minus"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
        <grid-row column-align="center" class="margin-top-xl">
          <grid-col col-size="4">
            <div class="variable-input-field submit-buttons align-right">
              <btn-pill
                :btnText="$t('commons.save')"
                v-on:click="onSave"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>
    </div>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts//MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import LabelInput from '@/components/shared/forms/LabelInput';
import LabelSelect from '@/components/shared/forms/LabelSelect';
import BtnPill from '@/components/shared/buttons/BtnPill';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import TableRow from '@/components/shared/table/components/TableRow';
import pagination from '@/components/shared/table/pagination';
import productsApi from 'BRAND/api/products';
import licenseApi from 'BRAND/api/license';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import Alert from '@/components/shared/alert/Alert';

export default {
  name: 'AddLicense',
  components: {
    MainLayout,
    Chevron,
    GridRow,
    GridCol,
    LabelInput,
    LabelSelect,
    BtnPill,
    TableComponent,
    TableColumn,
    TableRow,
    FontAwesomeIcon,
    Alert
  },
  data () {
    return {
      products: [],
      email: '',
      emailError: false,
      coupon: '',
      couponError: false,
      icon: {
        plus: faArrowUp,
        minus: faArrowDown
      },
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  mounted () {
    this.requestData();
  },
  watch: {
    getProducts: {
      handler: function () {
        this.$refs.productsTable.refresh();
      }
    }
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.workspace'),
          link: '/workspace'
        },
        {
          name: this.$t('menu.licenseManagement'),
          link: '/licenseManagement'
        },
        {
          name: this.$t('health.license.new'),
          link: '/addLicense',
          disabled: true
        }
      ];
    },
    getProducts () {
      return this.products;
    }
  },
  methods: {
    onSave () {
      this.clearErrors();
      if (this.email === '' || !isValidEmail(this.email)) {
        this.emailError = true;
        return;
      }
      const filteredProducts = this.products.filter(product => product.amount > 0);
      if (filteredProducts.length > 0) {
        const products = filteredProducts.map(product => {
          return {
            amount: product.amount,
            product_id: product.productId
          };
        });
        const order = {
          email: this.email,
          coupon: this.coupon,
          items: products
        };
        licenseApi.createLicenses(this, order).then(() => {
          const msg = this.$t('health.license.successProcessLicenses');
          this.$router.replace({name: 'LicenseManagement', params: {alert: msg}});
        }, () => {
          showAlert(this, this.$t('health.license.errorProcessLicenses'), 'error');
        });
      } else {
        // TODO: Show error of not added products
      }
    },
    clearErrors () {
      this.emailError = false;
      this.couponError = false;
    },
    isValidForm () {
      let valid = true;
      if (!this.email && isValidEmail(this.email)) {
        valid = false;
        this.emailError = true;
      }

      return valid;
    },
    addOne (product) {
      product.amount++;
      this.products = this.products.filter(item => item.id !== product.id);
      this.products.push(product);
    },
    substractOne (product) {
      if (product.amount > 0) {
        product.amount--;
        this.products = this.products.filter(item => item.id !== product.id);
        this.products.push(product);
      }
    },
    requestData () {
      productsApi.getProducts(this, true).then((data) => {
        this.products = data.map(product => {
          return {
            ...product,
            amount: 0
          };
        });
      });
    },
    fetchData ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getProducts,
        this.tableState,
        ['productId', 'entitlement', 'description'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const isValidEmail = (email) => {
  const regExp = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return regExp.test(String(email).toLowerCase());
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/AddLicense.vue